import { ActionIcon, Avatar, Menu } from '@mantine/core';
import { LogOutIcon, UserCogIcon, MailIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebaseConfig';
import { signOut } from 'firebase/auth';
import { AuthenticatedSession } from '@/authenticatedSession/authenticatedSession';
import { useCurrentUser } from '@/database/hooks/hooks';
import { telemetry } from '@/lib/telemetry';
import { useEmailAuthStatus } from '@/hooks/useEmailAuthStatus';

const UserMenu = ({ authSession }: { authSession: AuthenticatedSession }) => {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { authStatus: emailAuthStatus, refreshAuthStatus } =
    useEmailAuthStatus();

  const handleSignOut = async () => {
    if (!authSession) {
      navigate('/login');
      return;
    }

    telemetry.analyticsEvent({
      name: 'sign_out',
    });
    await signOut(auth);
  };

  const getEmailMenuLabel = () => {
    if (emailAuthStatus.status === 'loading') {
      return 'Loading...';
    }

    if (emailAuthStatus.status === 'authenticated') {
      return emailAuthStatus.email;
    }

    if (
      emailAuthStatus.status === 'unauthenticated' &&
      emailAuthStatus.provider
    ) {
      const providerName =
        emailAuthStatus.provider === 'google' ? 'Google' : 'Microsoft';
      return `Sign into ${providerName}`;
    }

    return 'Email Settings';
  };

  return (
    <Menu
      width={260}
      position='top-end'
      transitionProps={{ transition: 'pop-bottom-left' }}
      onOpen={() => {
        refreshAuthStatus();
      }}
      withinPortal
      zIndex={1000001}
    >
      <Menu.Target>
        <ActionIcon variant='subtle' style={{ cursor: 'pointer' }}>
          <Avatar
            variant='transparent'
            color='#999999'
            src={''}
            alt={''}
            radius='xl'
            size={40}
          />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Settings</Menu.Label>

        {currentUser && (
          <Menu.Item
            color='dark.8'
            leftSection={<UserCogIcon size={16} strokeWidth={1.5} />}
            onClick={() => {
              navigate('/my-settings');
            }}
          >
            My Settings
          </Menu.Item>
        )}
        <Menu.Item
          color='dark.8'
          leftSection={<MailIcon size={16} strokeWidth={1.5} />}
          onClick={() => {
            navigate('/email-account');
          }}
        >
          {getEmailMenuLabel()}
        </Menu.Item>
        <Menu.Item
          c='red'
          leftSection={<LogOutIcon size={16} strokeWidth={1.5} />}
          onClick={() => {
            handleSignOut();
          }}
        >
          Log out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};

export default UserMenu;
